// Words a person would actually say.
//
// Every sentence in this file is a function of a claim from
// `@wayfind/core`. That is the whole discipline: there is no string here
// that can be reached without the protocol having made the corresponding
// claim, so the interface cannot drift into confidence the wire never
// supported. "Nobody home", not "Delivery exception code 4".

import {
  STOP_REASON,
  type ArrivalClaim,
  type PositionClaim,
  type RunStatus,
  type StopReasonCode
} from "@wayfind/core";

const clock = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit" });

export function atClock(millis: number): string {
  return clock.format(new Date(millis));
}

/** "12 minutes", "1 minute", "under a minute" — never "0 min". */
export function minutesAway(seconds: number): string {
  if (seconds < 45) return "under a minute";
  const minutes = Math.round(seconds / 60);
  if (minutes <= 1) return "about a minute";
  if (minutes < 60) return `about ${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return `about ${hours} ${hours === 1 ? "hour" : "hours"}`;
  return `about ${hours}h ${rest}m`;
}

export function ageWords(seconds: number): string {
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${Math.round(seconds)} seconds ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  const hours = Math.round(minutes / 60);
  return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
}

/** §5.2.1's reason codes, in a sentence rather than a code. */
export function reasonWords(reason: StopReasonCode | null): string | null {
  switch (reason) {
    case STOP_REASON.ABSENT: return "nobody was in";
    case STOP_REASON.REFUSED: return "it was refused at the door";
    case STOP_REASON.INACCESSIBLE: return "the driver could not reach the door";
    case STOP_REASON.PARCEL_MISSING: return "the parcel was missing or damaged";
    case STOP_REASON.OTHER: return "the driver left a note";
    default: return null;
  }
}

/**
 * The same wire code, in the words of the run it belongs to.
 *
 * A bus does not deliver anything. §5.2.1's outcome 1 means "the driver
 * asserted this stop is done", and on a stops-only run that sentence is
 * "the bus has cleared stop 7" — which is the exact phrasing DESIGN.md
 * uses for what a parent at a kerb is told.
 */
export function outcomeWords(
  outcome: "delivered" | "skipped" | "failed" | "pending",
  coarse = false
): string {
  if (coarse) {
    switch (outcome) {
      case "delivered": return "Cleared";
      case "skipped": return "Not called at";
      case "failed": return "Missed";
      default: return "";
    }
  }
  switch (outcome) {
    case "delivered": return "Delivered";
    case "skipped": return "Skipped";
    case "failed": return "Could not be delivered";
    default: return "Waiting";
  }
}

/** What the position claim allows this screen to say about the vehicle. */
export function positionWords(position: PositionClaim): string {
  switch (position.kind) {
    case "live":
      return "The driver's position is current.";
    case "withheld":
      return "This run does not publish a position, so none is shown.";
    case "stale":
      return `The last position was ${ageWords(position.ageSeconds)}, so it is no longer shown.`;
    case "unplaceable":
      return "The driver is publishing a position, but there is no map here to place it on.";
    default:
      return "Nothing has been heard from the run yet.";
  }
}

export interface Headline {
  /** The short line above the number. */
  lede: string;
  /** The number itself, or a sentence when there is no number to give. */
  value: string;
  unit: string | null;
  /** Under the number. Always a fact, never reassurance. */
  sub: string | null;
  tone: "signal" | "settled" | "halt" | "muted";
}

/**
 * The one thing this screen says, from the arrival claim and nothing else.
 *
 * Each branch of the union produces exactly one shape, so a claim with no
 * time in it has no way to produce a headline with a time in it.
 */
export function headlineFor(
  arrival: ArrivalClaim,
  status: RunStatus,
  myStopIndex: number | null
): Headline {
  const isMine = myStopIndex != null;
  switch (arrival.kind) {
    case "estimate": {
      // A stale estimate is still an estimate — but it is an estimate
      // about a position that has aged, and the number must not be
      // presented as if the vehicle were being watched right now.
      if (arrival.stale) {
        return {
          lede: isMine ? "Last worked out" : "Last estimate",
          value: atClock(arrival.arrivalMillis),
          unit: null,
          sub: `From a position ${ageWords(arrival.observationAgeSeconds)}. Not live.`,
          tone: "muted"
        };
      }
      return {
        lede: isMine ? "Arriving about" : "Next stop about",
        value: atClock(arrival.arrivalMillis),
        unit: null,
        sub: `${capitalise(minutesAway(arrival.secondsFromNow))} away${
          arrival.stopsAway > 1 ? ` · ${arrival.stopsAway - 1} ${arrival.stopsAway === 2 ? "stop" : "stops"} before yours` : ""
        }`,
        tone: "signal"
      };
    }
    case "no-earlier-than": {
      // The floor a recipient can compute from their own door (§20.2).
      // The number is real and routed; what it cannot know is the
      // doorsteps in between, and those only ever push it later. So the
      // lede carries the hedge — "not before" — and the line underneath
      // explains why in words a person would actually use, rather than
      // burying a qualifier in the number itself.
      if (arrival.stale) {
        return {
          lede: "Not before",
          value: atClock(arrival.arrivalMillis),
          unit: null,
          sub: `Worked out from a position ${ageWords(arrival.observationAgeSeconds)}. Not live.`,
          tone: "muted"
        };
      }
      const away = minutesAway(arrival.secondsFromNow);
      return {
        lede: "Not before",
        value: atClock(arrival.arrivalMillis),
        unit: null,
        sub: arrival.stopsAhead === 0
          ? `${capitalise(away)} away, and yours is the next stop.`
          : `${arrival.stopsAhead} ${arrival.stopsAhead === 1 ? "stop" : "stops"} before yours`
            + ` — ${away} away if the van came straight here.`,
        tone: "signal"
      };
    }
    case "scheduled":
      return {
        lede: "Timetabled for",
        value: atClock(arrival.arrivalMillis),
        unit: null,
        sub: "From the published timetable. The run has not been heard from.",
        tone: "muted"
      };
    case "served": {
      // The wire carries no per-stop timestamp. The record that *did* the
      // marking has one, so a time is only shown when the newest record is
      // that mark — otherwise "delivered" stands on its own rather than
      // borrowing a clock reading from an unrelated record.
      const markedNow = status.lastOutcome?.stopIndex != null
        && status.lastOutcome.stopIndex === myStopIndex
        && status.observed.atUnixSeconds != null;
      return {
        lede: markedNow ? "Delivered at" : "Your stop",
        value: markedNow ? atClock(status.observed.atUnixSeconds! * 1000) : "Delivered",
        unit: null,
        sub: "The driver marked it delivered. That stays true whatever happens next.",
        tone: "settled"
      };
    }
    case "not-coming": {
      const reason = reasonWords(arrival.reasonCode);
      return {
        lede: "Your stop",
        value: arrival.outcome === "skipped" ? "Skipped" : "Not delivered",
        unit: null,
        // §9.1 — there is no arrival time here, and this sentence must
        // never imply one is coming later.
        sub: reason
          ? `${capitalise(reason)}. The driver is not coming back on this round.`
          : "The driver is not coming back on this round.",
        tone: "halt"
      };
    }
    case "no-map":
      return {
        lede: "Arrival time",
        value: "Not available here",
        unit: null,
        sub: "This device has no map of the area, so it cannot work one out. "
          + "Everything the run itself reports is below.",
        tone: "muted"
      };
    default:
      return {
        lede: status.observed.ever ? "Arrival time" : "Not started",
        value: status.observed.ever ? "Not yet known" : "Waiting to begin",
        unit: null,
        sub: status.observed.ever
          ? "The run has not said enough to work one out."
          : "You will see the arrival time here as soon as the run starts.",
        tone: "muted"
      };
  }
}

export function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** §12's row, in a sentence a person can act on. */
export function basisWords(arrival: ArrivalClaim): string {
  switch (arrival.kind) {
    case "estimate":
      return arrival.metric === "live-traffic"
        ? "Worked out on this device, over live traffic."
        : "Worked out on this device, on the map's own speeds — no live traffic here yet.";
    case "no-earlier-than":
      return arrival.metric === "live-traffic"
        ? "Worked out on this device, over live traffic, from the van straight to your address. "
          + "The stops in between are not in the link — nobody sent them here — so the real time can only be later."
        : "Worked out on this device, on the map's own speeds, from the van straight to your address. "
          + "The stops in between are not in the link, so the real time can only be later.";
    case "scheduled":
      return "The published timetable. Nothing live is behind it.";
    case "no-map":
      return "No map of this area on this device, so nothing was worked out.";
    default:
      return "No arrival time is being claimed.";
  }
}

/** §9.2 — the profile the number was computed under, when it matters. */
export function profileWords(arrival: ArrivalClaim): string | null {
  if (arrival.kind !== "estimate") return null;
  const { basis, graph, run } = arrival.profile;
  if (basis === "matched") return null; // nothing to warn about
  if (basis === "mismatched") {
    return `This run travels by ${run}, and the only map here is for ${graph}. `
      + "The time above is the wrong shape of journey and will read optimistic.";
  }
  return "The map here does not say what kind of journey it measures, so no claim is made either way.";
}
